import type { MeshRoleRules } from '../utils/meshRoleClassifier';

export type CameraPresetId =
  | 'beauty_3_4'
  | 'side_profile'
  | 'rear_3_4'
  | 'front_fascia'
  | 'top_down'
  | 'wheel_hub';

export type LightingPresetId =
  | 'industrial_shop'
  | 'showroom_spotlight'
  | 'daylight';

/**
 * Camera presets are an orbit description rather than a fixed position, because
 * the silhouettes differ in size by more than a metre and a hand-tuned [x,y,z]
 * frames one of them and crops the rest. They are resolved against the loaded
 * model's measured bounds at runtime by solveCameraPose() in
 * utils/cameraPoseSolver.ts.
 */
export interface CameraPreset {
  id: CameraPresetId;
  label: string;
  /** Horizontal orbit angle in degrees; 0 views the car broadside along +Z. */
  azimuthDegrees: number;
  /** Vertical orbit angle in degrees; 0 is level with the car, 90 is overhead. */
  elevationDegrees: number;
  /** Multiplier on the fitted distance. Below 1 pulls in for detail shots. */
  distanceFactor: number;
  /** Target height as a fraction of model height above its centre. */
  targetHeightFactor: number;
}

export const CAMERA_PRESETS: Record<CameraPresetId, CameraPreset> = {
  beauty_3_4: {
    id: 'beauty_3_4',
    label: '3/4 Beauty',
    azimuthDegrees: 40,
    elevationDegrees: 12,
    distanceFactor: 1.0,
    targetHeightFactor: 0.05,
  },
  side_profile: {
    id: 'side_profile',
    label: 'Profile Stance',
    azimuthDegrees: 0,
    elevationDegrees: 8,
    distanceFactor: 1.05,
    targetHeightFactor: 0.05,
  },
  rear_3_4: {
    id: 'rear_3_4',
    label: 'Rear 3/4',
    azimuthDegrees: 215,
    elevationDegrees: 12,
    distanceFactor: 1.0,
    targetHeightFactor: 0.05,
  },
  front_fascia: {
    id: 'front_fascia',
    label: 'Front Fascia',
    azimuthDegrees: 90,
    elevationDegrees: 6,
    distanceFactor: 0.95,
    targetHeightFactor: 0.0,
  },
  top_down: {
    id: 'top_down',
    label: 'Overhead CAD',
    azimuthDegrees: 0,
    elevationDegrees: 85,
    distanceFactor: 1.1,
    targetHeightFactor: 0.0,
  },
  wheel_hub: {
    id: 'wheel_hub',
    label: 'Wheel Detail',
    azimuthDegrees: 35,
    elevationDegrees: 10,
    distanceFactor: 0.35,
    targetHeightFactor: -0.25,
  },
};

export interface ModelConfig {
  path: string;
  /**
   * Nominal overall length in metres. The scale actually applied is DERIVED from
   * this and the loaded model's measured bounds, not authored by hand: the
   * bundled assets disagree wildly on units (the Shelby and Mustang are
   * centimetres, the truck is decimetres), so a fixed `scale` renders some cars
   * 400 m long and the truck knee-high.
   */
  realLengthMeters: number;
  scale?: number;
  rotationY: number;
  offsetY: number;
  wheelbaseMeters: number;
  bodyKeywords: string[];
  wheelKeywords: string[];
  brakeKeywords: string[];
  tireKeywords: string[];
  glassKeywords: string[];
  meshRoleRules: MeshRoleRules;
  /**
   * True when this silhouette is served by another model's geometry. The picker
   * and the viewport HUD badge these, so the UI never implies an asset is
   * present when it is not.
   */
  isPlaceholderMesh?: boolean;
  /**
   * When true, wheel corners are assigned by world-space quadrant instead of by
   * node name, for assets that merge all four wheels into one primitive.
   */
  usesQuadrantWheelDetection?: boolean;
  /** Note surfaced in the console when a model's mesh layout limits the editor. */
  meshLayoutNote?: string;
}

export const MODEL_CONFIGS: Record<string, ModelConfig> = {
  classic_muscle: {
    path: '/models/cars/shelby-gt500.glb',
    realLengthMeters: 4.78,
    rotationY: 0,
    offsetY: 0.01,
    wheelbaseMeters: 2.74,
    bodyKeywords: ['body', 'carpaint', 'door', 'hood', 'trunk', 'fender', 'bumper', 'quarter'],
    wheelKeywords: ['wheel', 'tire', 'rim'],
    brakeKeywords: ['brake', 'caliper', 'disc'],
    tireKeywords: ['tire'],
    glassKeywords: ['sideglass', 'tinted_glass', 'screen'],
    meshRoleRules: {
      bodyKeywords: ['body', 'carpaint', 'door', 'hood', 'trunk', 'fender', 'bumper', 'quarter'],
      wheelKeywords: ['wheel', 'rim'],
      tireKeywords: ['tire'],
      brakeKeywords: ['brake', 'caliper', 'disc'],
      glassKeywords: ['sideglass', 'tinted_glass', 'screen'],
    },
  },
  camaro_ss: {
    path: '/models/cars/camaro/scene.gltf',
    realLengthMeters: 4.72,
    rotationY: 0,
    offsetY: 0.02,
    wheelbaseMeters: 2.74,
    bodyKeywords: ['body', 'matte_3', 'carpaint'],
    wheelKeywords: ['wheel', 'rim'],
    brakeKeywords: ['break', 'brake', 'disc'],
    tireKeywords: ['tire'],
    glassKeywords: ['glass', 'window'],
    meshRoleRules: {
      bodyKeywords: ['body', 'matte_3', 'carpaint'],
      wheelKeywords: ['wheel', 'wheels_3', 'rim'],
      tireKeywords: ['tire'],
      brakeKeywords: ['break', 'brake', 'disc'],
      // 'Interior_03' is a single mesh covering the cabin; without it the
      // interior keeps its authored texture and fights the paint change.
      glassKeywords: ['glass', 'window', 'interior_03', 'lights_1_8'],
    },
  },
  vintage_truck: {
    path: '/models/cars/truck.glb',
    realLengthMeters: 5.40,
    rotationY: -Math.PI / 2,
    offsetY: 0.01,
    wheelbaseMeters: 2.92,
    bodyKeywords: ['car', 'truck', 'body'],
    wheelKeywords: ['wheel', 'tire', 'rim'],
    brakeKeywords: ['brake', 'disc'],
    tireKeywords: ['tire'],
    glassKeywords: ['glass', 'window'],
    meshRoleRules: {
      bodyKeywords: ['car', 'truck', 'body'],
      wheelKeywords: ['wheel', 'rim'],
      tireKeywords: ['tire'],
      brakeKeywords: ['brake', 'disc'],
      glassKeywords: ['glass', 'window'],
    },
    meshLayoutNote: 'Merged single-mesh asset: the wheels are baked into one mesh with one material, so stance and wheel sliders have no separate geometry to drive on this silhouette.',
  },
  mustang_fastback: {
    path: '/models/cars/mustang-1969.glb',
    realLengthMeters: 4.79,
    rotationY: 0,
    offsetY: 0.01,
    wheelbaseMeters: 2.74,
    bodyKeywords: ['carpaint', 'material_15', 'material', 'black', 'chrome'],
    wheelKeywords: ['tire', 'wheel', 'rim'],
    brakeKeywords: ['brakedisk', 'brake', 'disc'],
    tireKeywords: ['tire'],
    glassKeywords: ['windowglass', 'clearglass', 'redglass', 'orangeglass'],
    meshRoleRules: {
      // Node names here are generic (Object_0 ... Object_58), so the material
      // names are the only thing that identifies a part on this asset.
      //
      // 'carpaint' is only 4 primitives; the bulk of the visible bodywork sits
      // in 'black' (18), 'chrome' (19) and 'material'/'material_15' (10). All of
      // them take the paint, or the paint rack moves a sliver of the car and the
      // rest stays its authored grey. 'chrome' is deliberately included: on this
      // asset it is body sheet metal, not brightwork.
      bodyKeywords: ['carpaint', 'material_15', 'material', 'black', 'chrome'],
      wheelKeywords: ['tire', 'wheel', 'rim'],
      tireKeywords: ['tire'],
      brakeKeywords: ['brakedisk', 'brake', 'disc'],
      glassKeywords: ['windowglass', 'clearglass', 'redglass', 'orangeglass'],
    },
    usesQuadrantWheelDetection: true,
  },
  deuce_roadster: {
    path: '/models/cars/shelby-gt500.glb',
    realLengthMeters: 4.65,
    rotationY: 0,
    offsetY: 0.01,
    wheelbaseMeters: 2.69,
    bodyKeywords: ['body', 'carpaint', 'door', 'hood', 'fender'],
    wheelKeywords: ['wheel', 'tire', 'rim'],
    brakeKeywords: ['brake'],
    tireKeywords: ['tire'],
    glassKeywords: ['sideglass', 'tinted_glass', 'screen'],
    meshRoleRules: {
      bodyKeywords: ['body', 'carpaint', 'door', 'hood', 'fender'],
      wheelKeywords: ['wheel', 'rim'],
      tireKeywords: ['tire'],
      brakeKeywords: ['brake'],
      glassKeywords: ['sideglass', 'tinted_glass', 'screen'],
    },
    isPlaceholderMesh: true,
    meshLayoutNote: 'No 1932 Deuce asset is bundled; this silhouette renders the Shelby fastback geometry as a stand-in.',
  },
};